import { initializeApp, getApp, getApps, type FirebaseApp } from "firebase/app";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getAuth, type Auth } from "firebase/auth";
import { getAnalytics, isSupported, logEvent as firebaseLogEvent, type Analytics } from "firebase/analytics";

let app: FirebaseApp | undefined;
let db: Firestore | undefined;
let auth: Auth | undefined;
let analytics: Analytics | undefined;

// Initialize Firebase asynchronously
async function initializeFirebase(): Promise<{ db: Firestore; auth: Auth; analytics?: Analytics }> {
  if (app && db && auth) return { db, auth, analytics }; // Return existing instances if already initialized

  try {
    let config = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
    };

    // Only try to fetch from Netlify if we're missing any config values and not in dev
    if ((!config.apiKey || !config.projectId) && !import.meta.env.DEV) {
      try {
        const baseUrl = window.location.origin;
        const response = await fetch(`${baseUrl}/.netlify/functions/get-firebase-config`);
        if (!response.ok) {
          throw new Error(`Failed to get Firebase config: ${response.status}`);
        }
        config = await response.json();
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Failed to fetch Firebase config from Netlify. Using environment variables.');
        }
      }
    }

    // Validate that we have the minimum required config
    if (!config.apiKey || !config.projectId) {
      throw new Error('Firebase configuration is incomplete. Please check your environment variables.');
    }

    // Initialize Firebase as a singleton
    app = getApps().length ? getApp() : initializeApp(config);
    db = getFirestore(app);
    auth = getAuth(app);

    // Initialize Analytics only when explicitly enabled and supported
    if (typeof window !== 'undefined') {
      try {
        const enableAnalytics = String(import.meta.env.VITE_ENABLE_ANALYTICS).toLowerCase() === 'true';
        const hasMeasurementId = !!import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;
        if (enableAnalytics && hasMeasurementId) {
          const supported = await isSupported();
          if (supported) {
            analytics = getAnalytics(app);
            try {
              firebaseLogEvent(analytics, 'app_start', { timestamp: Date.now() });
            } catch (e) {
              if (import.meta.env.DEV) console.warn('Analytics event failed:', e);
            }
          }
        } else {
          // Explicitly disable analytics to avoid network calls / 403s in production
          analytics = undefined;
        }
      } catch (e) {
        if (import.meta.env.DEV) console.warn('Analytics not supported:', e);
      }
    }

    // One-time debug log of which project is active (helps diagnose cross-project 403s)
    try {
      const opts: any = app.options || {};
      // Only log in dev to avoid leaking config in prod logs
      if (import.meta.env.DEV) {
        const apiKeyTail = (opts.apiKey || '').slice(-6);
        console.log('[Firebase] Project:', opts.projectId, 'ApiKey…' + apiKeyTail);
      }
    } catch {}

    return { db, auth, analytics } as { db: Firestore; auth: Auth; analytics?: Analytics };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to initialize Firebase:', error);
    }
    throw error;
  }
}

// Helper function to log events
export const logEvent = (eventName: string, eventParams?: Record<string, any>) => {
  if (analytics) {
    try {
      firebaseLogEvent(analytics, eventName, eventParams);
    } catch (e) {
      console.warn('Failed to log event:', eventName, e);
    }
  }
};

// Export the initialization function and instances
export { initializeFirebase, db, auth, analytics };
